import i18n from 'i18next';
import numbro from 'numbro';
import { initReactI18next } from 'react-i18next';
import { continueLaterLocalization } from '@vwfs-its/sf-sac-frontend';
import { merge } from 'lodash';

import base from './resources/localization/translations.de-DE.json';
import chi from './resources/localization/chi.json';
import { appConfig } from './config';
import { BrandType } from '@bus/models';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const languages = require('numbro/dist/languages.min.js');

const appLanguage = appConfig.language;

// -------------- load numbro.js settings --------------------
numbro.registerLanguage(languages[appLanguage]);
numbro.setLanguage(appLanguage);

type TranslationFile = {
  [appLang: string]: {
    'common': Record<string, unknown>
    [brands: string]: Record<string, unknown>
  }
}

const loadLocalizationResources = (...jsonFiles: Array<TranslationFile>) => {
  return merge(
    {},
    ...jsonFiles.map(file => {
      return [
        file[appLanguage]?.common ?? {},
        file[appLanguage]?.[appConfig.brandName] ?? {}
      ];
    }).flat()
  );
};

const resources = loadLocalizationResources(base, chi);

const continueLaterResources = continueLaterLocalization({
  locale: 'de',
  brandName: {
    BRAND_NAME: appConfig.brandName
  },
  dataProtectionLink: '' // TODO: link
});

const isSeatOrCupraBrand = [BrandType.SEAT, BrandType.CUPRA].includes(
  appConfig.brandName as BrandType
);

const tanValidation = {
  requiredTan: 'TAN ist nicht korrekt. Bitte erneut eingeben.',
  tanLength: 'TAN ist nicht korrekt. Bitte erneut eingeben.',
  requiredEmail: 'E-Mail Adresse ist eine Pflichtangabe.',
  ...(isSeatOrCupraBrand && {
    requiredPhone: 'Bitte gib deine Mobilfunknummer ein.'
  })
};

const continueLaterOverride = isSeatOrCupraBrand
  ? {
    continueLater: {
      ...continueLaterResources.continueLater,
      save: {
        ...continueLaterResources.continueLater.save,
        title: 'Möchtest du mit deinem Antrag später fortfahren?',
        textLine1: 'Durch Klick auf "Link senden", erhältst du von uns einen Link per E-Mail, über den du deinen Antrag später wieder aufrufen kannst.',
        textLine3: 'Klicke auf "Schließen", um mit deinem Antrag fortzufahren.',
        validityText: 'Bitte beachte, dass der Link nur bis zu 30 Tage gültig ist!'
      },
      form: {
        ...continueLaterResources.continueLater.form,
        customerTelephoneNrTooltip: 'Wir benötigen deine Mobilfunknummer, da wir dir per SMS eine TAN zusenden, mit der du deinen Antrag erneut aufrufen kannst.'
      }
    }
  } : {
    continueLater: {
      ...continueLaterResources.continueLater,
      save: {
        ...continueLaterResources.continueLater.save,
        validityText: 'Bitte beachten Sie, dass der Link nur bis zu 30 Tage gültig ist!'
      }
    }
  };



resources['sf-sac'] = merge({}, resources['sf-sac'] ?? {}, continueLaterResources, {
  tooltip: 'Speichern für später',
  tanAuthentication: {
    heading: 'Wilkommen Zurück',
    content: isSeatOrCupraBrand ?
      'Gebe bitte die TAN ein, die wir Dir per SMS zugeschickt haben.' :
      'Geben Sie bitte die TAN ein, die wir Ihnen per SMS zugeschickt haben.',
    inlineErrorMessage: 'TAN ist nicht korrekt. Bitte erneut eingeben.'
  },
  validation: tanValidation,
  ...continueLaterOverride
});

// eslint-disable-next-line import/no-named-as-default-member
i18n.use(initReactI18next).init({
  lng: appLanguage,
  fallbackLng: appLanguage,

  resources: {
    [appLanguage]: resources
  },

  // disable debug in production and test
  // disabling debug in testing gives us less chatter
  debug:
    'production' !== process.env.NODE_ENV && 'test' !== process.env.NODE_ENV,

  // have a common namespace used around the full app
  ns: ['translations'],
  defaultNS: 'translations',

  // handle translation keys that can't be found. if keys has suffix "tooltip",
  // return empty string. otherwise return key. This is to support show/hide
  // tooltip based on its value, at the meantime avoid some weird behaviors like
  // empty validation error message
  parseMissingKeyHandler: (key) => (key.endsWith('tooltip') ? '' : key),

  keySeparator: '.', // we use hierarchically nested translations

  interpolation: {
    escapeValue: false,
    format: function (value, format) {
      if (format === 'currency')
        return value !== null
          ? numbro(value).formatCurrency({
            mantissa: 2,
            thousandSeparated: true,
            optionalMantissa: true,
            currencyPosition: 'postfix',
            spaceSeparated: true,
            spaceSeparatedCurrency: true
          })
          : '';

      if (format === 'number')
        return value !== null
          ? numbro(value).format({ output: 'number', thousandSeparated: true })
          : '';

      if (format === 'percentage')
        return value !== null
          ? numbro(value).format({ output: 'percent', mantissa: 2 })
          : '';

      return value;
    }
  }
});

export default i18n;
